import { type ReactNode, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type GanttView } from '@apm/contracts';
import { ChevronDown, Flag, MoreHorizontal } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { GlassSegmented } from '../../../components/ui/glass-segmented';
import { EmptyState, ErrorState, Skeleton } from '../../../components/ui/states';
import { errorMessage } from '../../../lib/error-message';
import { cn } from '../../../lib/utils';
import { useAuth } from '../../auth/auth-context';
import { TaskDetailDrawer } from '../../collaboration/components/task-detail-drawer';
import { TaskProgressSlot } from '../../tasks/components/task-progress-slot';
import { boardApi } from '../../board/api';
import { type BoardFilter, NO_BOARD_FILTER, isFilterActive } from '../../board/filter';
import { TaskFormDialog } from '../../tasks/components/task-form-dialog';
import { useTestRoundMarkers } from '../../testing/round-markers';
import { ganttApi, ganttKeys } from '../api';
import { ReschedulePanel } from './reschedule-panel';
import { type useReschedule } from '../use-reschedule';
import { type DateRange } from '../drag';
import { type Zoom } from '../scale';
import { GanttChart } from './gantt-chart';
import { TaskGroupBar } from './task-group-bar';

/** Bốn thao tác nhóm — gộp để chỉ có một chỗ nạp lại và một chỗ báo lỗi. */
type GroupAction =
  | { kind: 'create'; name: string; taskIds: string[] }
  | { kind: 'rename'; groupId: string; name: string }
  | { kind: 'delete'; groupId: string }
  | { kind: 'assign'; taskIds: string[]; groupId: string | null };

const ZOOMS: Zoom[] = ['day', 'week', 'month'];

interface GanttPanelProps {
  /**
   * Lượt "Xếp lịch bằng AI" — APM-1313b.
   *
   * Nút bấm nằm ở thanh công cụ của `TasksPanel` (cạnh "Việc mới") còn bảng
   * duyệt nằm ở đây, nên state phải do tổ tiên chung giữ. Xem `useReschedule`.
   */
  readonly aiReschedule: ReturnType<typeof useReschedule>;
  readonly projectId: string;
  /** Ngày kết thúc dự án — vẽ thành vạch deadline. `null` = chưa đặt. */
  readonly deadline?: string | null;
  /**
   * `task:update` ở dự án này — quyền **hạn**, không phải chế độ giao diện.
   *
   * Mở nút Sửa và ô cập nhật tiến độ trong popup chi tiết.
   */
  readonly canEdit?: boolean;
  /**
   * Chế độ "Sửa biểu đồ" đang bật — APM-GANTT-n.
   *
   * ⚠️ Tách hẳn khỏi `canEdit`, và đó là chỗ đã sai một lần. Trước đây
   * `TasksPanel` truyền `canEdit={canEditTasks && ganttEditing}`, nên tắt "Sửa
   * biểu đồ" thì popup chi tiết cũng mất luôn nút Sửa và nút Đóng việc — một
   * công tắc về *cách kéo thả trên biểu đồ* âm thầm rút mất quyền của người
   * dùng ở một màn hình khác.
   *
   * Hai câu hỏi khác nhau: "người này có được sửa việc không" (quyền) và "cú
   * kéo chuột lúc này nghĩa là sửa hay là xem" (chế độ). Chỉ câu thứ hai thuộc
   * về cái công tắc ấy.
   */
  readonly chartEditable?: boolean;
  /** `ai_suggestion:approve`: áp dụng được phương án AI xếp lại (APM-1313). */
  readonly canApprove?: boolean;
  /**
   * Thả một việc từ ngăn kéo lập lịch xuống biểu đồ — APM-BOARD-c.
   *
   * Panel chỉ chuyển tiếp: hai lời gọi (vào sprint, đặt ngày) do `TasksPanel`
   * thực hiện, vì chỉ nó biết sprint nào đang xem.
   */
  readonly onDropTask?: (taskId: string, startDate: string) => void;
  /**
   * Chỉ vẽ task của sprint này — APM-BOARD-b, đồng bộ với Board và Danh sách.
   *
   * Bỏ trống = cả dự án (dự án Waterfall, hoặc chưa chọn sprint nào).
   */
  readonly sprintId?: string | undefined;
  /** Kỳ của dự án bảo trì — APM-MAINT-e. Bỏ trống = không lọc theo thời gian. */
  readonly period?: string | null;
  /**
   * Bộ lọc/làm nổi dùng chung với Board và Danh sách — APM-BOARD-h.
   *
   * Gantt trước đây **không có bộ lọc nào** ngoài "chỉ đường găng": trên một dự
   * án hai trăm việc, câu hỏi "việc của Bảo nằm ở đâu trên trục thời gian" chỉ
   * trả lời được bằng cách dò từng hàng.
   */
  readonly filter?: BoardFilter;
}

/**
 * Gantt kèm thanh công cụ — APM-503.
 *
 * Mọi thao tác là chuột: chọn mức phóng, bật/tắt lọc đường găng, chốt baseline,
 * đổi task thành mốc, qua cổng pha. Không ô nhập ngày nào trong màn hình này.
 */
export function GanttPanel({
  projectId,
  deadline = null,
  canEdit = false,
  chartEditable = false,
  canApprove = false,
  onDropTask,
  sprintId,
  period = null,
  filter = NO_BOARD_FILTER,
  aiReschedule,
}: GanttPanelProps): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [zoom, setZoom] = useState<Zoom>('week');
  const [criticalOnly, setCriticalOnly] = useState(false);
  // Chỉ có nghĩa ở điện thoại — màn rộng chú giải luôn mở (`max-sm:hidden`).
  const [legendOpen, setLegendOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openedId, setOpenedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  /** Task đang tích để gom nhóm — APM-1306. */
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());

  const [offset, setOffset] = useState(0);
  const [showAll, setShowAll] = useState(false);
  // Global filters retain the full result until their indexed window is requested.
  const windowed = !showAll && !criticalOnly && !isFilterActive(filter);
  const key = [...ganttKeys.view(projectId, sprintId, period), windowed ? offset : 'all'];
  const gantt = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      ganttApi.view(projectId, sprintId, period, windowed ? offset : undefined, signal),
  });

  /**
   * Nuốt kết quả của một endpoint đổi lịch vào cache — APM-GANTT-q.
   *
   * ## ⚠️ Chỉ nuốt được khi đang xem CẢ dự án
   *
   * Mọi endpoint đổi lịch trả kèm một `GanttView`, và trước đây nó được ghi
   * thẳng vào `key` để đỡ một vòng gọi. Nhưng `GanttController.ganttOf()` dựng
   * view ấy bằng `gantt(projectId)` — **không có sprint**. Nên khi đang lọc
   * theo một sprint, kéo một thanh sẽ ghi view của *cả dự án* vào ô cache của
   * *một sprint*, và cả những việc thuộc sprint khác đổ ra màn hình. Đúng báo
   * cáo ở HRM sprint 6, kéo HRM-17.
   *
   * Sửa ở client chứ không ở server: cho năm endpoint kia nhận thêm `sprintId`
   * nghĩa là năm chỗ có thể quên truyền, và quên thì lỗi quay lại y nguyên —
   * im lặng, vì view trả về vẫn hợp lệ, chỉ là của phạm vi khác.
   *
   * Không nuốt thì tốn thêm một vòng gọi cho mỗi cú kéo trong chế độ sprint.
   * Đổi lại là một danh sách luôn đúng phạm vi người dùng đã chọn.
   */
  function absorb(view: GanttView): void {
    if (!windowed && sprintId === undefined && period === null) queryClient.setQueryData(key, view);
    else void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: ['board', projectId] });
  }

  const reschedule = useMutation({
    mutationFn: ({ taskId, range }: { taskId: string; range: DateRange }) =>
      ganttApi.reschedule(taskId, range.startDate, range.dueDate),
    onSuccess: (view, variables) => {
      absorb(view);
      const bar = view.bars.find((item) => item.task.id === variables.taskId);
      // Cảnh báo, không chặn: lịch đã được ghi, chỉ là nó vi phạm phụ thuộc.
      if (bar && bar.violationDays > 0) {
        toast.warning(t('gantt.violationToast', { days: bar.violationDays }));
      }
    },
    onError: (error) => {
      toast.error(errorMessage(error, t));
      // Kéo hỏng thì trả thanh về đúng chỗ cũ ngay, đừng để nó nằm sai.
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  /**
   * Cập nhật tiến độ từ popup — APM-TASK-h.
   *
   * `PATCH /tasks/:id/progress` trả về `TaskView`, không phải `GanttView`, nên
   * **không** dùng được `absorb`. Phải làm mới cả query: `%` mới đổi cả thanh
   * tiến độ trong biểu đồ lẫn `progressPct` của nhóm cha, và không có đường nào
   * suy ra chúng từ một `TaskView` lẻ.
   */
  const progress = useMutation({
    mutationFn: ({
      taskId,
      value,
      actualStartAt,
    }: {
      taskId: string;
      value: number;
      actualStartAt?: string;
    }) => boardApi.setProgress(taskId, value, actualStartAt),
    onSuccess: () => toast.success(t('board.progressSaved')),
    onError: (error) => toast.error(errorMessage(error, t)),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: ['board', projectId] });
    },
  });

  /**
   * Nối / bỏ nối hai task.
   *
   * Nạp lại cả Gantt sau đó (khác `reschedule`, endpoint đó trả sẵn view):
   * thêm một phụ thuộc đổi đường găng và float của mọi task phía sau, nên một
   * cạnh mới không phải là thay đổi cục bộ.
   */
  /**
   * Xếp lại thứ tự hàng — APM-GANTT-h.
   *
   * Không cập nhật lạc quan: server đánh số lại **toàn bộ** dự án, nên dựng lại
   * kết quả ở client là viết lại đúng thuật toán ấy lần thứ hai — và hai bản sẽ
   * lệch nhau ở đúng chỗ khó thấy nhất (hàng đang bị bộ lọc ẩn đi).
   */
  const reorder = useMutation({
    mutationFn: (body: { taskId: string; beforeTaskId: string | null }) =>
      ganttApi.reorder(projectId, body),
    onError: (error) => toast.error(errorMessage(error, t)),
    /*
      ⚠️ `key`, không phải `ganttKeys.view(projectId)` — APM-GANTT-p.

      Khoá có cả sprint (`['gantt', p, sprintId]`), nên bỏ sprint đi cho ra
      `['gantt', p, null]`: một khoá **khác**, không phải tiền tố của khoá đang
      gắn. React Query không khớp, không nạp lại, và biểu đồ giữ nguyên thứ tự
      cũ cho tới lần refetch tình cờ nào đó — rồi mới nhảy. Ở dự án Waterfall
      (sprint `null`) thì trùng nhau nên lỗi ẩn hoàn toàn.
    */
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const link = useMutation({
    mutationFn: ({ fromTaskId, toTaskId }: { fromTaskId: string; toTaskId: string }) =>
      ganttApi.addDependency(projectId, fromTaskId, toTaskId),
    onSuccess: () => toast.success(t('gantt.linked')),
    onError: (error) => toast.error(errorMessage(error, t)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const unlink = useMutation({
    mutationFn: (dependencyId: string) => ganttApi.removeDependency(dependencyId),
    onSuccess: () => toast.success(t('gantt.unlinked')),
    onError: (error) => toast.error(errorMessage(error, t)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  /**
   * Bốn thao tác nhóm gộp vào một mutation.
   *
   * Chỉ một chỗ nạp lại dữ liệu, và một chỗ hiện lỗi: thứ tự hàng do server
   * xếp theo nhóm, nên mọi thao tác nhóm đều phải tải lại cả Gantt.
   */
  const groups = useMutation({
    mutationFn: async (action: GroupAction): Promise<void> => {
      switch (action.kind) {
        case 'create':
          await ganttApi.createGroup(projectId, action.name, action.taskIds);
          return;
        case 'rename':
          await ganttApi.renameGroup(action.groupId, action.name);
          return;
        case 'delete':
          await ganttApi.deleteGroup(action.groupId);
          return;
        case 'assign':
          await ganttApi.assignGroup(projectId, action.taskIds, action.groupId);
          return;
      }
    },
    onSuccess: (_result, action) => {
      if (action.kind !== 'rename') setSelectedIds(new Set());
      toast.success(t(`gantt.group${action.kind[0]!.toUpperCase()}${action.kind.slice(1)}Done`));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: key }),
        queryClient.invalidateQueries({ queryKey: ['board', projectId] }),
      ]);
    },
  });

  /**
   * Ảnh hưởng của lịch — APM-1312.
   *
   * Query riêng và **không chặn** việc vẽ Gantt: nó đọc lịch làm việc, đợt nghỉ,
   * ngày lễ và cam kết của từng người, nên nó tới sau. Thiếu nó thì biểu đồ chỉ
   * mất màu cảnh báo, không mất thanh nào.
   */
  const impact = useQuery({
    queryKey: ganttKeys.impact(projectId),
    queryFn: () => ganttApi.impact(projectId),
    // Không thử lại: 403 ở đây nghĩa là không có quyền đọc dự án, và thử ba lần
    // chỉ làm Gantt chờ lâu hơn để nhận cùng một câu.
    retry: false,
  });

  /*
    Vạch mốc vòng test — APM-TEST-h, docs/plan-testing-tab.md §9.3 mức 2.

    Cũng không chặn việc vẽ: chưa có vòng nào, hoặc người xem không có
    `test:read`, thì Gantt mất vạch chứ không mất thanh nào.
  */
  const roundMarkers = useTestRoundMarkers(projectId);

  const baseline = useMutation({
    mutationFn: () => ganttApi.setBaseline(projectId),
    onSuccess: (view) => {
      absorb(view);
      toast.success(t('gantt.baselineSaved'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (gantt.isPending) return <Skeleton className="h-96 w-full" />;
  if (gantt.isError) {
    return (
      <ErrorState message={errorMessage(gantt.error, t)} onRetry={() => void gantt.refetch()} />
    );
  }

  const view = gantt.data;
  if (view.bars.length === 0 && !view.page?.total) {
    return <EmptyState title={t('gantt.emptyTitle')} description={t('gantt.emptyDescription')} />;
  }

  const opened = view.bars.find((bar) => bar.task.id === openedId) ?? null;
  const editing = view.bars.find((bar) => bar.task.id === editingId) ?? null;

  const baselineLabel = view.baselineSetAt ? t('gantt.rebaseline') : t('gantt.setBaseline');

  return (
    <div className="flex flex-col gap-3 max-sm:gap-2">
      {/*
        Điện thoại: thanh công cụ còn **hai hàng mảnh** — người dùng yêu cầu
        2026-10-07. Bản rộng xếp thành bốn hàng ở 390px (phóng, đường găng, chốt
        kế hoạch gốc, rồi ba câu chú giải) trước khi thấy thanh Gantt nào.

        - "Chốt kế hoạch gốc" vào menu ⋯: bấm vài lần mỗi dự án, không đáng
          một chỗ thường trực.
        - Chú giải gập sau nút "Chú giải màu": người mở Gantt lần thứ mười
          không cần đọc lại bảng màu, còn người lần đầu vẫn tìm thấy nó ngay
          cạnh con số đường găng.
      */}
      <div className="flex flex-wrap items-center gap-2 max-sm:gap-x-1.5 max-sm:gap-y-1">
        <GlassSegmented
          size="sm"
          value={zoom}
          options={ZOOMS.map((value) => ({ value, label: t(`gantt.zoom.${value}`) }))}
          onChange={setZoom}
        />

        <Button
          size="sm"
          variant={criticalOnly ? 'default' : 'outline'}
          aria-pressed={criticalOnly}
          onClick={() => setCriticalOnly((value) => !value)}
        >
          {t('gantt.criticalOnly')}
        </Button>

        <Button
          size="sm"
          variant="outline"
          className="max-sm:hidden"
          disabled={baseline.isPending}
          onClick={() => baseline.mutate()}
        >
          {baselineLabel}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="w-8 px-0 sm:hidden"
              aria-label={t('gantt.moreActions')}
            >
              <MoreHorizontal aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled={baseline.isPending} onSelect={() => baseline.mutate()}>
              <Flag aria-hidden className="size-4" />
              {baselineLabel}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Ngắt hàng ở điện thoại: hàng hai là thứ để **đọc**, không phải bấm. */}
        <div aria-hidden className="basis-full sm:hidden" />
        <Button
          size="sm"
          variant="ghost"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground sm:hidden"
          aria-expanded={legendOpen}
          onClick={() => setLegendOpen((open) => !open)}
        >
          {t('gantt.legendToggle')}
          <ChevronDown
            aria-hidden
            className={cn('transition-transform', legendOpen && 'rotate-180')}
          />
        </Button>

        {/*
          Độ trượt đứng cạnh độ dài đường găng: hai con số trả lời cùng một câu
          hỏi ("dự án này dài bao nhiêu") — một theo kế hoạch, một theo thực tế
          lịch người làm. Đặt xa nhau thì không ai so chúng.
        */}
        {/*
          Trần danh sách đã cắt bớt thì **nói ra**: một biểu đồ tô 20 thanh trong
          khi có 30 việc bị ảnh hưởng đọc y như "chỉ có 20 thôi", và PM sẽ lập
          kế hoạch trên một phần sự thật.
        */}
        {impact.data && impact.data.omittedCount > 0 ? (
          <span className="ml-auto text-xs text-muted-foreground">
            {t('impact.omitted', { count: impact.data.omittedCount })}
          </span>
        ) : null}

        {impact.data && impact.data.slipDays > 0 ? (
          <span
            className={cn(
              'text-xs font-medium text-rag-red',
              impact.data.omittedCount === 0 && 'ml-auto',
            )}
          >
            {t('impact.slip', {
              days: impact.data.slipDays,
              date: impact.data.projectedEndDate,
            })}
          </span>
        ) : null}

        <span
          className={cn(
            'text-xs text-muted-foreground',
            !(impact.data && (impact.data.slipDays > 0 || impact.data.omittedCount > 0)) &&
              'ml-auto',
          )}
        >
          {t('gantt.criticalLength', { days: view.criticalPathDays })}
        </span>
      </div>

      {/*
        Chú giải màu — APM-GANTT-l.

        Biểu đồ dùng bốn màu để nói bốn chuyện khác nhau, và không có chú giải
        thì mỗi màu là một câu đố: xanh lá là "đã xong" hay "đúng tiến độ"? Đỏ
        là "trễ" hay "vi phạm phụ thuộc"? Người xem đoán, và đoán sai thì đọc
        sai cả biểu đồ.

        Nằm dưới thanh công cụ chứ không trong nó: thanh công cụ là chỗ **bấm**,
        chú giải là chỗ **đọc**, và nhét thêm năm ô vào một hàng đã có `ml-auto`
        cân hai đầu thì hàng ấy vỡ ở màn hình hẹp.
      */}
      <ul
        className={cn(
          'flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground',
          !legendOpen && 'max-sm:hidden',
        )}
      >
        {(
          [
            ['bg-primary', 'gantt.legendOnTrack'],
            ['bg-rag-amber', 'gantt.legendBehind'],
            ['bg-rag-red', 'gantt.legendOverdue'],
            ['bg-rag-green/70', 'gantt.legendDone'],
            ['bg-rag-red/50', 'gantt.legendDoneLate'],
          ] as const
        ).map(([swatch, label]) => (
          <li key={label} className="flex items-center gap-1.5">
            {/*
              Lớp màu viết sẵn thành hằng, không ghép lúc chạy: Tailwind quét mã
              nguồn để biết sinh lớp nào, nên `'bg-' + name` cho ra một ô trong
              suốt (bẫy đã ghi ở CLAUDE.md).
            */}
            <span aria-hidden className={cn('size-2.5 rounded-sm', swatch)} />
            {t(label)}
          </li>
        ))}

        {/*
          Ô thứ năm **nhấp nháy**, đúng như trên biểu đồ — APM-GANTT-o.

          Lịch sai là chuyện khác loại với bốn mức trạng thái, nên nó phải ở một
          kênh khác. Ba bản trước thử màu đỏ (đụng nghĩa "quá hạn"), rồi gạch
          chéo — nhưng tín hiệu tĩnh nào trên một biểu đồ đã kín màu cũng phải
          cạnh tranh với mọi thứ quanh nó. Chuyển động thì không.
        */}
        <li className="flex items-center gap-1.5">
          <span aria-hidden className="gantt-violation-legend size-2.5 rounded-sm" />
          {t('gantt.legendViolation')}
        </li>
      </ul>

      {/*
        Khối "Nhờ AI xếp lại" đứng **trên** biểu đồ: nó chỉ hiện khi có việc mất
        người, và lúc đó nó là thứ PM cần đọc trước cả các thanh.
      */}
      <ReschedulePanel canApply={canApprove} state={aiReschedule} />

      <TaskGroupBar
        count={selectedIds.size}
        groups={view.groups}
        pending={groups.isPending}
        onCreate={(name) => groups.mutate({ kind: 'create', name, taskIds: [...selectedIds] })}
        onAssign={(groupId) =>
          groups.mutate({ kind: 'assign', taskIds: [...selectedIds], groupId })
        }
        onDetach={() => groups.mutate({ kind: 'assign', taskIds: [...selectedIds], groupId: null })}
        onClear={() => setSelectedIds(new Set())}
      />

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {view.page && (
          <>
            <span>
              {t('gantt.pageRows', {
                from: view.page.offset + 1,
                to: view.page.offset + view.bars.length,
                total: view.page.total,
              })}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={view.page.offset === 0}
              onClick={() => {
                setOffset(Math.max(0, view.page!.offset - view.page!.limit));
                setOpenedId(null);
              }}
            >
              {t('common.previous')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={view.page.offset + view.page.limit >= view.page.total}
              onClick={() => {
                setOffset(view.page!.offset + view.page!.limit);
                setOpenedId(null);
              }}
            >
              {t('common.next')}
            </Button>
            {view.page.offPageLinks > 0 && (
              <span>{t('gantt.offPageLinks', { count: view.page.offPageLinks })}</span>
            )}
          </>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setShowAll(!showAll);
            setOffset(0);
          }}
        >
          {t(showAll ? 'gantt.pagedRows' : 'gantt.allRows')}
        </Button>
      </div>

      <GanttChart
        view={view}
        zoom={zoom}
        markers={roundMarkers}
        impact={impact.data ?? null}
        criticalOnly={criticalOnly}
        filter={filter}
        selectedId={selectedId}
        deadline={deadline}
        onSelect={setSelectedId}
        onOpen={setOpenedId}
        onReschedule={(taskId, range) => reschedule.mutate({ taskId, range })}
        editable={chartEditable}
        {...(onDropTask ? { onDropTask } : {})}
        {
          /*
          Nối/bỏ nối/xếp lại đòi **cả hai**: có quyền, và đang ở chế độ sửa biểu
          đồ. Chúng là những thao tác chỉ thực hiện được bằng cách kéo trên biểu
          đồ, nên chúng thuộc về công tắc ấy — khác với nút Sửa và nút Đóng việc
          trong popup, thứ chỉ hỏi quyền.
        */ ...(canEdit && chartEditable
            ? {
                onLink: (fromTaskId: string, toTaskId: string) =>
                  link.mutate({ fromTaskId, toTaskId }),
                onUnlink: (dependencyId: string) => unlink.mutate(dependencyId),
                onReorder: (taskId: string, beforeTaskId: string | null) =>
                  reorder.mutate({ taskId, beforeTaskId }),
                selectedIds,
                onToggleSelect: (taskId: string, selected: boolean) =>
                  setSelectedIds((current) => {
                    const next = new Set(current);
                    if (selected) next.add(taskId);
                    else next.delete(taskId);
                    return next;
                  }),
                onRenameGroup: (groupId: string, name: string) =>
                  groups.mutate({ kind: 'rename', groupId, name }),
                onDeleteGroup: (groupId: string) => groups.mutate({ kind: 'delete', groupId }),
              }
            : {})
        }
      />

      {/* Chi tiết task: cùng drawer với board, nên bình luận và lịch sử của
          một task là một chỗ duy nhất bất kể mở từ đâu. */}
      {opened && user ? (
        <TaskDetailDrawer
          task={opened.task}
          currentUserId={user.id}
          canEdit={canEdit}
          onClose={() => setOpenedId(null)}
          /*
            Cập nhật tiến độ ngay trong popup — APM-TASK-h.

            Gantt là chỗ người ta **nhìn thấy** việc mình đang chậm; bắt họ đi
            sang board hay "Việc của tôi" để kéo một con số là ba cú bấm cho
            một thao tác lẽ ra mất hai giây.

            `canEdit` ở đây đã là `task:update` trên **đúng dự án đang mở**
            (`tasks-panel` truyền xuống), nên không phải hỏi lại quyền.
          */
          progress={
            <TaskProgressSlot
              task={opened.task}
              canEdit={canEdit}
              onChange={(value, actualStartAt) =>
                progress.mutate({
                  taskId: opened.task.id,
                  value,
                  ...(actualStartAt ? { actualStartAt } : {}),
                })
              }
            />
          }
          {...(canEdit
            ? {
                /*
                  Nút Sửa từ Gantt — APM-TASK-e.

                  Trước đây drawer ở đây nhận `canEdit` nhưng **không** nhận
                  `onEdit`, nên nút không bao giờ hiện. Với task thường thì còn
                  sửa được từ board; với **mốc (milestone)** thì không: mốc chỉ
                  vẽ trên Gantt, nên đó là chỗ duy nhất mở nó ra được — và cũng
                  là chỗ duy nhất không cho sửa.

                  Đóng drawer khi mở form, cùng lý do đã ghi ở board: hai lớp
                  phủ chồng nhau thì Esc đóng cái nào là chuyện đoán, và cái ở
                  dưới vẫn bắt được cú bấm ra ngoài.
                */
                onEdit: () => {
                  setEditingId(opened.task.id);
                  setOpenedId(null);
                },
              }
            : {})}
        />
      ) : null}

      {editing ? (
        <TaskFormDialog
          projectId={projectId}
          task={editing.task}
          canApprove={canApprove}
          onClose={() => setEditingId(null)}
          onSaved={() => setEditingId(null)}
        />
      ) : null}

      {/*
        Thanh chi tiết ở đáy đã bỏ — APM-GANTT-k.

        Nó chiếm một dải ngang suốt chiều rộng để nói ba thứ mà chỗ khác đã nói:
        mã và tên việc có ngay trên hàng, còn dự phòng/đường găng nằm trong
        tooltip của chính thanh. Đổi lấy chiều cao — thứ mà biểu đồ Gantt thiếu
        nhất, vì mỗi hàng bớt đi là một việc phải cuộn mới thấy.

        Hai nút của nó không mất: đổi loại việc↔mốc nay ở form sửa task
        (APM-TASK-f), còn "Qua cổng pha" chuyển về danh sách cổng pha trong tab
        Báo cáo — màn hình dành riêng cho cổng pha, nơi nó nằm cạnh số điều kiện
        đã xong.

        "Suy ra phụ thuộc còn thiếu" cũng đã dọn khỏi đây — APM-UX-ai6. Nó
        từng đứng cố định ở chân trang, chiếm chỗ dù không ai đang cần. Nay là
        một mục trong popup "Sửa nhanh" ở thanh công cụ tab Công việc
        (`InferDependenciesDialog`), cạnh "Sửa hàng loạt bằng lời".
      */}
    </div>
  );
}
